import { useEffect, useState, useRef } from "react";
import ChatMessage from "./chat/CommentArea";
import HeartButton from "./heart/HeartButton";
import ShopButton from "./shop/ShopButton";
import { PlayerMeta, ProductModel, PusherConfig, StreamerInfo } from "@/types";
import Pusher from "pusher-js";
import { IoMdEye, IoMdPerson } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { MdLanguage } from "react-icons/md";
import i18n from "@/i18n";

export interface HeaderProps {
  title: string;
  views: number | null;
  pusher: Pusher | null;
  pusherConfig: PusherConfig | null;
  streamer?: StreamerInfo;
}

const Header = ({
  title,
  views,
  pusher,
  pusherConfig,
  streamer,
}: HeaderProps) => {
  const { t } = useTranslation();
  const [viewCount, setViewCount] = useState<number | null>(views);
  const [showStreamerInfo, setShowStreamerInfo] = useState<boolean>(false);
  const [modalWidth, setModalWidth] = useState<number>(192); // デフォルトの幅（w-48 = 12rem = 192px）
  const headerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState<boolean>(false);

  const languages = [
    { code: "ja", label: "日本語" },
    { code: "en", label: "English" },
    { code: "zh", label: "中文" },
  ];

  useEffect(() => {
    setViewCount(views);
  }, [views]);

  useEffect(() => {
    if (pusher && pusherConfig) {
      const prefix = pusherConfig.prefix + "-";
      const joinNotice = pusher.subscribe(prefix + "notice");
      joinNotice.bind(prefix + "notice-event", async function (data: any) {
        setViewCount(data.total);
      });
    }
  }, [pusher]);

  useEffect(() => {
    if (showStreamerInfo && headerRef.current) {
      const headerWidth = headerRef.current.getBoundingClientRect().width;
      setModalWidth(Math.min(headerWidth * 0.8, 384)); // 最大幅を384px（w-96）に制限
    }
  }, [showStreamerInfo]);

  useEffect(() => {
    const checkOverflow = () => {
      if (titleRef.current) {
        const isOverflowing = titleRef.current.scrollWidth > titleRef.current.clientWidth;
        setShouldScroll(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [title]);

  const handleLanguageChange = async (langCode: string) => {
    try {
      await i18n.changeLanguage(langCode);
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  return (
    <div
      ref={headerRef}
      className="tw-w-full tw-h-full tw-flex tw-justify-between tw-items-center tw-text-white tw-font-sans tw-pt-[20px]"
    >
      <div className="tw-w-2/3 tw-flex tw-items-center tw-px-2">
        {streamer && (
          <div className="tw-relative">
            <button
              className="tw-w-6 tw-h-6 tw-rounded-full tw-overflow-hidden tw-mr-2 tw-border tw-border-white/50 hover:tw-border-white tw-transition-colors"
              onClick={() => setShowStreamerInfo(true)}
            >
              <img
                src={streamer.iconUrl}
                alt={streamer.name}
                className="tw-w-full tw-h-full tw-object-cover"
              />
            </button>
            {showStreamerInfo && (
              <div
                className="tw-absolute tw-top-full tw-left-0 tw-mt-2 tw-bg-white/90 tw-rounded-lg tw-p-4 tw-z-50 tw-text-black tw-shadow-lg"
                style={{ width: `${modalWidth}px` }}
              >
                <div className="tw-flex tw-items-center tw-mb-3">
                  <img
                    src={streamer.iconUrl}
                    alt={streamer.name}
                    className="tw-w-10 tw-h-10 tw-rounded-full tw-mr-3"
                  />
                  <div>
                    <h3 className="tw-font-bold tw-text-base">
                      {streamer.homepage ? (
                        <a
                          href={streamer.homepage}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {streamer.name}
                        </a>
                      ) : (
                        streamer.name
                      )}
                    </h3>
                  </div>
                </div>
                <p className="tw-text-xs tw-text-gray-600">
                  {streamer.description}
                </p>
                <div className="tw-space-y-2 tw-text-sm">
                  {streamer.privacyPolicyUrl && (
                    <a
                      href={streamer.privacyPolicyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-block tw-text-blue-600 tw-text-xs hover:tw-text-blue-800"
                    >
                      プライバシーポリシー
                    </a>
                  )}
                </div>
                <button
                  className="tw-absolute tw-top-2 tw-right-2 tw-text-gray-500 hover:tw-text-gray-700"
                  onClick={() => setShowStreamerInfo(false)}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
        <div
          ref={titleRef}
          className={`tw-overflow-hidden tw-whitespace-nowrap ${shouldScroll ? '' : 'tw-truncate'}`}
        >
          {shouldScroll ? (
            <div className="tw-animate-marquee tw-inline-block tw-pl-full">
              {title}
              <span className="tw-px-6">{title}</span>
            </div>
          ) : (
            title
          )}
        </div>
      </div>
      { viewCount !== null && (
        <div className="tw-w-1/3 tw-text-right tw-pr-4 tw-flex tw-items-center tw-justify-end">
          <div className="tw-flex tw-items-center">
            <IoMdEye className="tw-inline tw-mr-1" size="1em" />
            <span>{t("player.views", { count: viewCount })}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
